<script setup lang="ts">
import { useFormValidation } from '../../../../composables/useFormValidation'
import { Amount, required } from '../../../../utils/validation'
import TextInput from '../../../../components/ui/form/TextInput.vue'
import CheckInput from '../../../../components/ui/form/CheckInput.vue'
import { api } from '../../../../api/Api'
import Toast from '../../../../services/Toast'
import { Permission } from '../../../../types/auth'
import SearchSelect from '../../../../components/ui/form/SearchSelect.vue'
import { ref, onMounted } from 'vue'
import ModalFormTemplate from '../../../../components/ui/Templates/ModalFormTemplate.vue'
import AmountInput from '../../../../components/ui/form/AmountInput.vue'
import DateInput from '../../../../components/ui/form/DateInput.vue'

const props = defineProps<{
  data?: any | null
  mode: 'create' | 'edit' | 'view' | 'showQuickView' | 'toggleStatus'
  title: string | null
  endPoint: string,
  permissions?: Permission[],
  listCreateName?: string
}>()

const emit = defineEmits<{
  (e: 'save', data: any): void
  (e: 'close'): void
}>()

const { fields, formData, setFieldRules, validateForm, resetForm, setFormData } = useFormValidation({
  code: props.data?.code || "",
    name: props.data?.name || props.listCreateName || "",
    value: props.data?.value || 0,
    image: props.data?.image || "",
    acquisition_date: props.data?.acquisition_date || "",
    asset_groups_id: props.data?.asset_groups_id || "",
    asset_templates_id: props.data?.asset_templates_id || "",
    method: props.data?.method || "",
    duration: props.data?.duration || "",
    duration_type: props.data?.duration_type || "",
    computation: props.data?.computation || "",
    not_depreciated_value_p: props.data?.not_depreciated_value_p || 0,
    fixed_asset_accounts_id: props.data?.fixed_asset_accounts_id || "",
    depreciation_accounts_id: props.data?.depreciation_accounts_id || "",
    expense_accounts_id: props.data?.expense_accounts_id || "",
    prorata_date: props.data?.prorata_date || "",
    depreciated_amount: props.data?.depreciated_amount || 0,
    active: props.data?.active || true,
    uid: props.data?.uid || "",
})

setFieldRules("code", [required("Code is required")]);
setFieldRules("name", [required("name is required")]);
setFieldRules("value", [
  required("Value is required"),
  Amount("Value cannot be 0"),
]);
setFieldRules("acquisition_date", [required("Acquisition Date is required")]);
setFieldRules("method", [required("Method is required")]);
setFieldRules("duration", [required("Duration is required")]);
setFieldRules("duration_type", [required("Duration Type is required")]);
setFieldRules("computation", [required("Computation is required")]);
setFieldRules("fixed_asset_accounts_id", [
  required("Fixed Asset Account is required"),
]);
setFieldRules("depreciation_accounts_id", [
  required("Depreciation Account is required"),
]);
setFieldRules("expense_accounts_id", [required("Expense Account is required")]);
setFieldRules("depreciated_amount", [
  required("Depreciated Amount is required"),
  Amount("Depreciated Amount cannot be 0"),
]);

const handleAssetTemplate = (template: any) => {
  if (template) {
    fields.value.method.value =  template.method;
    fields.value.duration.value = template.duration;
    fields.value.duration_type.value = template.duration_type;
    fields.value.computation.value = template.computation;
    fields.value.depreciation_accounts_id.value = template.depreciation_accounts_id;
    fields.value.expense_accounts_id.value = template.expense_accounts_id;
    fields.value.not_depreciated_value_p.value = template.not_depreciated_value_p;
  }
};

const methodList = [
  { id: "Straighline", name: "Straighline" },
  { id: "Declining", name: "Declining" },
];

const durationTypeList = [
  { id: "Months", name: "Months" },
  { id: "Years", name: "Years" },
];

const computationList = [
  { id: "No Prorata", name: "No Prorata" },
  { id: "Constant Periods", name: "Constant Periods" },
  { id: "Based on Days per Period", name: "Based on Days per Period" },
];



const handleSubmit = async (close: boolean, newRecord: boolean) => {
  if (validateForm()) {
    const action = formData.value.uid != '' ? 'update' : 'create'
    const response = await api.post((props.endPoint.slice(-1) == '/' ? props.endPoint : props.endPoint + '/') + action, formData.value)
    if (response.success == true) {
      Toast.show('saved successfully')
      emit('save', response.data)
      if (close) {
        emit('close')
      } else if (newRecord) {
        resetForm()
      } else {
        setFormData(response.data)
      }
    }
  }
}

const isReadOnly = ref(true);
onMounted(() => {
  isReadOnly.value = props.mode == 'view'
  if (props.mode == 'create') {
      resetForm()
    } else if (props.data) {
      setFormData(props.data)
    }
})


</script>
<template>
  <ModalFormTemplate :data="data" :mode="mode" :title="title" :endPoint="endPoint" :perm="permissions"
    :listCreateName="listCreateName" :tabList="[]" :formData="formData" @save="handleSubmit"
    @close="$emit('close')">
    <template #form>
      <div class="grid grid-cols-1 md:flex gap-6">
        <TextInput v-model="fields.code.value" label="Code" placeholder="Enter Code" class="md:w-1/3"
          :readonly="isReadOnly" :error="fields.code.touched ? fields.code.error : null"
          @blur="fields.code.touched = true" />

        <TextInput v-model="fields.name.value" class="md:w-2/3" label="Name" placeholder="Enter Name"
          :readonly="isReadOnly" :error="fields.name.touched ? fields.name.error : null"
          @blur="fields.name.touched = true" />
      </div>
      <div class="grid md:grid-cols-3 gap-6">
        <AmountInput v-model="fields.value.value" label="Asset Value" :readonly="isReadOnly"
          :error="fields.value.touched ? fields.value.error : null" @blur="fields.value.touched = true" />

        <DateInput v-model="fields.acquisition_date.value" label="Acquisition Date" :readonly="isReadOnly" :error="fields.acquisition_date.touched
            ? fields.acquisition_date.error
            : null
          " @blur="fields.acquisition_date.touched = true" />

        <SearchSelect v-model="fields.asset_groups_id.value" endPoint="finance/accounting/asset-groups"
          label="Asset Group" :readonly="isReadOnly" :error="fields.asset_groups_id.touched ? fields.asset_groups_id.error : null
            " @blur="fields.asset_groups_id.touched = true" />

        <SearchSelect v-model="fields.asset_templates_id.value" label="Asset Template"
          end-point="finance/accounting/asset-templates" @select="handleAssetTemplate" :readonly="isReadOnly" :error="fields.asset_templates_id.touched
              ? fields.asset_templates_id.error
              : null
            " @blur="fields.asset_templates_id.touched = true" />

        <SearchSelect v-model="fields.method.value" :options="methodList" label="Method" :readonly="isReadOnly"
          :error="fields.method.touched ? fields.method.error : null" @blur="fields.method.touched = true" />

        <SearchSelect v-model="fields.computation.value" :options="computationList" label="Computation"
          :readonly="isReadOnly" :error="fields.computation.touched ? fields.computation.error : null"
          @blur="fields.computation.touched = true" />

        <AmountInput v-model="fields.duration.value" label="Duration" :readonly="isReadOnly"
          :error="fields.duration.touched ? fields.duration.error : null" @blur="fields.duration.touched = true" />

        <SearchSelect v-model="fields.duration_type.value" :options="durationTypeList" label="Duration Type"
          :readonly="isReadOnly" :error="fields.duration_type.touched ? fields.duration_type.error : null
            " @blur="fields.duration_type.touched = true" />

        <AmountInput v-model="fields.not_depreciated_value_p.value" label="Not Depreciated Value (%)"
          :readonly="isReadOnly" :error="fields.not_depreciated_value_p.touched
              ? fields.not_depreciated_value_p.error
              : null
            " @blur="fields.not_depreciated_value_p.touched = true" />

        <SearchSelect v-model="fields.fixed_asset_accounts_id.value" label="Fixed Asset Account"
          end-point="finance/accounting/chart-of-accounts" :readonly="isReadOnly" :error="fields.fixed_asset_accounts_id.touched
              ? fields.fixed_asset_accounts_id.error
              : null
            " @blur="fields.fixed_asset_accounts_id.touched = true" />

        <SearchSelect v-model="fields.depreciation_accounts_id.value" end-point="finance/accounting/chart-of-accounts"
          label="Depreciation Account" :readonly="isReadOnly" :error="fields.depreciation_accounts_id.touched
              ? fields.depreciation_accounts_id.error
              : null
            " @blur="fields.depreciation_accounts_id.touched = true" />

        <SearchSelect v-model="fields.expense_accounts_id.value" end-point="finance/accounting/chart-of-accounts"
          label="Expense Account" :readonly="isReadOnly" :error="fields.expense_accounts_id.touched
              ? fields.expense_accounts_id.error
              : null
            " @blur="fields.expense_accounts_id.touched = true" />

        <DateInput v-model="fields.prorata_date.value" label="Prorata Date" :readonly="isReadOnly" :error="fields.prorata_date.touched ? fields.prorata_date.error : null
          " @blur="fields.prorata_date.touched = true" />

        <AmountInput v-model="fields.depreciated_amount.value" label="Depreciated Amount" :readonly="isReadOnly" :error="fields.depreciated_amount.touched
            ? fields.depreciated_amount.error
            : null
          " @blur="fields.depreciated_amount.touched = true" />

        <CheckInput v-model="fields.active.value" :readonly="isReadOnly" label="Active"
          :error="fields.active.touched ? fields.active.error : null" @blur="fields.active.touched = true" />
      </div>
    </template>
  </ModalFormTemplate>
</template>